import { Mail01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, useForm } from "@inertiajs/react";
import type { FormEvent } from "react";
import Dashboard from "@/components/dashboard";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	errors?: Record<string, string[]>;
}

export default function Edit({ user, errors }: Props) {
	const { data, setData, patch, processing } = useForm({
		email: user.email,
		password_challenge: "",
	});

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		patch("/identity/email");
	};

	return (
		<>
			<Head title="Změna e-mailu" />
			<Dashboard user={user}>
				<div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
					<div className="flex items-center gap-3">
						<div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
							<HugeiconsIcon
								icon={Mail01Icon}
								strokeWidth={2}
								className="size-5 text-primary"
							/>
						</div>
						<div>
							<h2 className="font-semibold text-2xl">Změna e-mailu</h2>
							<p className="text-muted-foreground text-sm">
								Aktualizujte svou e-mailovou adresu
							</p>
						</div>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>Nový e-mail</CardTitle>
							<CardDescription>
								Pro změnu e-mailu zadejte novou adresu a své heslo pro
								potvrzení.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<form onSubmit={handleSubmit}>
								<FieldGroup>
									<Field>
										<FieldLabel htmlFor="email">Nový e-mail</FieldLabel>
										<Input
											id="email"
											name="email"
											type="email"
											value={data.email}
											onChange={(e) => setData("email", e.target.value)}
											aria-invalid={!!errors?.email}
											required
											autoComplete="email"
										/>
										{errors?.email && (
											<FieldDescription className="text-destructive">
												{errors.email.join(", ")}
											</FieldDescription>
										)}
									</Field>

									<Field>
										<FieldLabel htmlFor="password_challenge">
											Současné heslo
										</FieldLabel>
										<Input
											id="password_challenge"
											name="password_challenge"
											type="password"
											value={data.password_challenge}
											onChange={(e) =>
												setData("password_challenge", e.target.value)
											}
											aria-invalid={!!errors?.password_challenge}
											required
											autoComplete="current-password"
										/>
										{errors?.password_challenge && (
											<FieldDescription className="text-destructive">
												{errors.password_challenge.join(", ")}
											</FieldDescription>
										)}
									</Field>

									<Field>
										<Button type="submit" disabled={processing}>
											Změnit e-mail
										</Button>
									</Field>
								</FieldGroup>
							</form>
						</CardContent>
					</Card>

					<p className="text-muted-foreground text-xs">
						Po změně e-mailu vám bude zaslán ověřovací e-mail na novou adresu.
					</p>
				</div>
			</Dashboard>
		</>
	);
}
